import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { isApiError } from "@/services/Api";
import { recordEvidenceCountService } from "@/services";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useLoggingStore } from "@/stores/logging-store";

import type { RecordEvidenceCount } from "@/models/RecordEvidenceCount";

interface State {
  moduleStatus: ModuleStatus;
  evidenceCounts: readonly RecordEvidenceCount[];
}
export const useEvidenceCountStore = defineStore("evidenceCount", {
  scope: "case",
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
    evidenceCounts: [],
  }),
  getters: {},
  actions: {
    async load(): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.evidenceCounts === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        this.setStatus(ModuleStatus.Loading);

        const counts = await recordEvidenceCountService.getAll(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
        );
        this.setEvidenceCounts(counts);
      } catch (error) {
        if (isApiError(error) && error.status === 204) {
          this.setEvidenceCounts([]);
          return;
        }

        useLoggingStore().Error({
          message: "Failed to load user session into module",
          data: error as object,
        });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    setStatus(status: ModuleStatus): void {
      this.moduleStatus = status;
    },
    setEvidenceCounts(payload: RecordEvidenceCount[]): void {
      this.evidenceCounts = Object.freeze(payload);
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.evidenceCounts = [];
    },
  },
});
